import { useLayoutEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NavigationCustomizationItemDto } from '@asset-metrix/portal-gateway-client-js';
import { useQuery } from '@tanstack/react-query';

import {
  CustomPageFilterQueryKey,
  fetchCustomPageFilterData,
} from '../../api/portalGateway/customPageFilterData';
import {
  fetchPageFilterData,
  PageFilterQueryKey,
} from '../../api/portalGateway/pagefilterData';
import { useStore } from '../../store';
import { useEffectiveGlobalSelections } from '../../store/hooks/useEffectiveGlobalSelections';
import { useSelections } from '../../store/hooks/useSelections';
import { FilterSelections } from '../../store/states/filterSelection';
import { FilterConfigDto } from '../../store/states/filterTypes';
import {
  getDefaultComparisonPair,
  getValidComparisonPairFromUrl,
  isValidComparisonSelection,
  parseComparisonDiscriminatorValues,
  parseComparisonEntityPool,
  validateComparisonDetailPageUrl,
} from '../../utils/comparisonDetailPageEntities';
import { COMPARISON_ENTITIES_URL_PARAM } from '../../utils/constants';
import { prependRoleId } from '../../utils/prependRoleId';

import {
  EMPTY_FILTER_CONFIGS,
  useSanitizeSelections,
  validateFiltersFromResponse,
} from './filterQueryInternals';
import { useGlobalFilterLoaded } from './useGlobalFilterQuery';

/**
 * When the comparison selection is missing or invalid (for example after reset),
 * restore a valid pair. Prefer repeated discriminator keys from the URL when they
 * are present in `dp_entities`; otherwise fall back to the first two sorted pool
 * IDs — aligned with the gateway "filter by first value" behavior for resets.
 */
function useComparisonSelectionSanitizer(
  entityId: string,
  filterConfigs: FilterConfigDto[] | undefined,
  pageSelections: FilterSelections,
  searchParams: URLSearchParams
): void {
  const comparisonConfig = useMemo(
    () => filterConfigs?.find((f) => f.display === 'comparison'),
    [filterConfigs]
  );
  const isFilterInitialized = useStore(
    (state) => state.filterInitialized[entityId] ?? false
  );

  useLayoutEffect(() => {
    if (
      comparisonConfig == null ||
      !isFilterInitialized ||
      !searchParams.has(COMPARISON_ENTITIES_URL_PARAM)
    ) {
      return;
    }

    const currentSelection = pageSelections[comparisonConfig.key];
    if (isValidComparisonSelection(searchParams, currentSelection)) {
      return;
    }

    const resolvedPair =
      getValidComparisonPairFromUrl(searchParams, comparisonConfig.key) ??
      getDefaultComparisonPair(searchParams);
    if (resolvedPair == null) {
      return;
    }

    useStore
      .getState()
      .setFilterSelectionValue(entityId, comparisonConfig.key, resolvedPair);
  }, [
    comparisonConfig,
    entityId,
    isFilterInitialized,
    pageSelections,
    searchParams,
  ]);
}

/**
 * On comparison detail pages, overrides the comparison filter's selection with
 * all dp_entities (the full entity pool) so the page-filter API constrains
 * cascading filters and the comparison dropdown to every available entity
 * — not just the two currently selected for comparison.
 *
 * This override is merged into `effectiveSelections` only (the `selection`
 * body parameter). The WIAS endpoint reads multi-value selections from
 * `params.pageFilters` and applies them as WHERE-clause constraints.
 *
 * Widget data queries continue to use the standard filterSelections (the
 * selected pair) because they should scope data to the compared entities only.
 */
function useComparisonEntityPoolOverride(
  filterConfigs: FilterConfigDto[] | undefined,
  searchParams: URLSearchParams
): Record<string, string[]> | undefined {
  return useMemo(() => {
    if (!filterConfigs || !searchParams.has(COMPARISON_ENTITIES_URL_PARAM)) {
      return undefined;
    }

    const comparisonConfig = filterConfigs.find(
      (f) => f.display === 'comparison'
    );
    if (!comparisonConfig) {
      return undefined;
    }

    const entityIds = parseComparisonEntityPool(searchParams);
    if (entityIds.length === 0) {
      return undefined;
    }

    return { [comparisonConfig.key]: entityIds };
  }, [filterConfigs, searchParams]);
}

export function usePageFilterQuery(
  pageId: string,
  filterKey: string,
  filterConfigs: FilterConfigDto[] | undefined,
  activeDynamicNavigationItem?: NavigationCustomizationItemDto
) {
  const globalSelections = useEffectiveGlobalSelections();
  const pageSelections = useSelections(filterKey);
  const isInitialized = useStore(
    (state) => state.filterInitialized[filterKey] ?? false
  );
  const filterQueryLoaded = useStore(
    (state) => state.filterQueryLoaded[filterKey] ?? false
  );

  const globalFilterLoaded = useGlobalFilterLoaded();

  const hasFilters = filterConfigs != null && filterConfigs.length > 0;

  const [searchParams] = useSearchParams();
  const comparisonPoolOverride = useComparisonEntityPoolOverride(
    filterConfigs,
    searchParams
  );

  const effectiveSelections = useMemo(
    () =>
      comparisonPoolOverride
        ? { ...pageSelections, ...comparisonPoolOverride }
        : pageSelections,
    [pageSelections, comparisonPoolOverride]
  );

  const comparisonFilterKey = useMemo(
    () => filterConfigs?.find((f) => f.display === 'comparison')?.key,
    [filterConfigs]
  );

  const comparisonUrlValidationError = useMemo(() => {
    if (
      comparisonFilterKey == null ||
      !searchParams.has(COMPARISON_ENTITIES_URL_PARAM)
    ) {
      return undefined;
    }

    const discriminatorValues = parseComparisonDiscriminatorValues(
      searchParams,
      comparisonFilterKey
    );
    if (discriminatorValues.length === 0) {
      return undefined;
    }

    try {
      validateComparisonDetailPageUrl(searchParams, comparisonFilterKey);
      return undefined;
    } catch (error) {
      return error instanceof Error ? error : new Error(String(error));
    }
  }, [comparisonFilterKey, searchParams]);

  // Exclude comparison filter from sanitization so the dp_entities pool
  // override used in the query is never written back into the store.
  const sanitizableConfigs = useMemo(
    () =>
      (filterConfigs ?? EMPTY_FILTER_CONFIGS).filter(
        (fc) => fc.display !== 'comparison'
      ),
    [filterConfigs]
  );

  const query = useQuery({
    queryKey: prependRoleId<PageFilterQueryKey>([
      'pageFilters',
      { pageId, filterKey },
      effectiveSelections,
      globalSelections,
    ]),
    queryFn: fetchPageFilterData,
    enabled: hasFilters && isInitialized && globalFilterLoaded,
  });

  // Same contract as table filters: isSuccess stays false until the layout
  // effect has written first-value / pruned selections into the store.
  const isSanitized = useSanitizeSelections(
    query,
    filterKey,
    sanitizableConfigs,
    pageSelections,
    (sanitized) => {
      const withPool =
        comparisonFilterKey != null &&
        comparisonFilterKey in effectiveSelections
          ? {
              ...sanitized,
              [comparisonFilterKey]: effectiveSelections[comparisonFilterKey],
            }
          : sanitized;
      return prependRoleId<PageFilterQueryKey>([
        'pageFilters',
        { pageId, filterKey },
        withPool,
        globalSelections,
      ]);
    }
  );

  useComparisonSelectionSanitizer(
    filterKey,
    filterConfigs,
    pageSelections,
    searchParams
  );

  const { validatedData, validationError } = useMemo(() => {
    if (query.data == null) {
      return { validatedData: undefined, validationError: undefined };
    }
    try {
      return {
        validatedData: validateFiltersFromResponse(
          filterConfigs ?? [],
          query.data,
          activeDynamicNavigationItem
        ),
        validationError: undefined,
      };
    } catch (error) {
      return {
        validatedData: undefined,
        validationError:
          error instanceof Error ? error : new Error(String(error)),
      };
    }
  }, [query.data, filterConfigs, activeDynamicNavigationItem]);

  // When called with real configs (authoritative): derive from query result.
  // When called without configs (status-check): defer to the store flag so
  // callers block until the authoritative query has completed.
  const isSuccess =
    filterConfigs === undefined
      ? filterQueryLoaded
      : !hasFilters || (query.isSuccess && isSanitized);

  useLayoutEffect(() => {
    if (filterConfigs === undefined) {
      return;
    }
    const state = useStore.getState();
    const shouldBeLoaded = !hasFilters || (query.isSuccess && isSanitized);
    if (shouldBeLoaded && !state.filterQueryLoaded[filterKey]) {
      state.setFilterQueryLoaded(filterKey);
    } else if (!shouldBeLoaded && state.filterQueryLoaded[filterKey]) {
      state.resetFilterQueryLoaded(filterKey);
    }
  }, [
    filterConfigs,
    hasFilters,
    isSanitized,
    query.isSuccess,
    filterKey,
    filterQueryLoaded,
  ]);

  if (comparisonUrlValidationError) {
    throw comparisonUrlValidationError;
  }

  if (validationError) {
    throw validationError;
  }

  return {
    data: query.data,
    error: query.error,
    isError: query.isError,
    isFetching: query.isFetching,
    isPending: query.isPending,
    isLoading: query.isLoading,
    isSuccess,
    validatedData,
  };
}

// ---------------------------------------------------------------------------
// Custom page filter query
// ---------------------------------------------------------------------------

export function useCustomPageFilterQuery(
  pageId: string,
  customFilterInstanceId: string,
  pageFilterInstanceId: string,
  activeCustomFilterConfigs: FilterConfigDto[]
) {
  const globalSelections = useEffectiveGlobalSelections();
  const pageSelections = useSelections(pageFilterInstanceId);
  const customPageSelections = useSelections(customFilterInstanceId);

  const globalFilterLoaded = useGlobalFilterLoaded();

  const pageQuery = usePageFilterQuery(pageId, pageFilterInstanceId, undefined);

  const hasFilters = activeCustomFilterConfigs.length > 0;

  const query = useQuery({
    queryKey: prependRoleId<CustomPageFilterQueryKey>([
      'customPageFilters',
      pageId,
      activeCustomFilterConfigs.map((el) => el.key),
      pageSelections,
      customPageSelections,
      globalSelections,
    ]),
    queryFn: fetchCustomPageFilterData,
    enabled: hasFilters && pageQuery.isSuccess && globalFilterLoaded,
  });

  const isSanitized = useSanitizeSelections(
    query,
    customFilterInstanceId,
    activeCustomFilterConfigs,
    customPageSelections,
    (sanitized) =>
      prependRoleId<CustomPageFilterQueryKey>([
        'customPageFilters',
        pageId,
        activeCustomFilterConfigs.map((el) => el.key),
        pageSelections,
        sanitized,
        globalSelections,
      ])
  );

  const { validatedData, validationError } = useMemo(() => {
    if (query.data == null) {
      return { validatedData: undefined, validationError: undefined };
    }
    try {
      return {
        validatedData: validateFiltersFromResponse(
          activeCustomFilterConfigs,
          query.data
        ),
        validationError: undefined,
      };
    } catch (error) {
      return {
        validatedData: undefined,
        validationError:
          error instanceof Error ? error : new Error(String(error)),
      };
    }
  }, [query.data, activeCustomFilterConfigs]);

  if (validationError) {
    throw validationError;
  }

  const filterQueryLoaded = useStore(
    (state) => state.filterQueryLoaded[customFilterInstanceId] ?? false
  );

  const isSuccess = !hasFilters || (query.isSuccess && isSanitized);

  useLayoutEffect(() => {
    const state = useStore.getState();
    const shouldBeLoaded = !hasFilters || (query.isSuccess && isSanitized);
    if (shouldBeLoaded && !state.filterQueryLoaded[customFilterInstanceId]) {
      state.setFilterQueryLoaded(customFilterInstanceId);
    } else if (
      !shouldBeLoaded &&
      state.filterQueryLoaded[customFilterInstanceId]
    ) {
      state.resetFilterQueryLoaded(customFilterInstanceId);
    }
  }, [
    hasFilters,
    isSanitized,
    query.isSuccess,
    customFilterInstanceId,
    filterQueryLoaded,
  ]);

  return {
    isFetching: query.isFetching,
    isSuccess,
    validatedData,
  };
}
